import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trackAuthError, trackSignInCompleted } from '@/services/analytics';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';
import { retryAsync } from '@/utils/retryAsync';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [500, 1000, 2000];

/**
 * OAuth callback page - handles token exchange race condition
 *
 * When Cognito redirects back with an authorization code, Amplify may not have
 * finished exchanging the code for tokens by the time this component mounts.
 * We retry checkAuthStatus() with exponential backoff to handle this race.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [result, setResult] = useState<'pending' | 'success' | 'failed'>('pending');

  // Retry-aware auth checking
  useEffect(() => {
    const controller = new AbortController();

    retryAsync(
      async () => {
        const { checkAuthStatus } = useAuthStore.getState();
        await checkAuthStatus();

        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          throw new Error('Not authenticated yet');
        }
      },
      { maxRetries: MAX_RETRIES, delays: RETRY_DELAYS, signal: controller.signal }
    )
      .then(() => {
        if (!controller.signal.aborted) {
          setResult('success');
        }
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError' && !controller.signal.aborted) {
          setResult('failed');
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  // Redirect based on auth outcome
  useEffect(() => {
    if (result === 'pending') return;

    const urlParams = new URLSearchParams(window.location.search);
    const identityProvider = urlParams.get('identity_provider')?.toLowerCase();
    const provider: 'apple' | 'google' | 'email' =
      identityProvider === 'signinwithapple'
        ? 'apple'
        : identityProvider === 'google'
          ? 'google'
          : 'email';

    if (result === 'success') {
      logger.debug('OAuth callback: user authenticated, redirecting to home');
      trackSignInCompleted(provider, false);
      navigate('/', { replace: true });
    } else {
      const { error } = useAuthStore.getState();
      logger.error('OAuth callback: authentication failed after retries');
      trackAuthError(provider, 'session_check', error || 'Authentication failed after callback');
      navigate('/login', { replace: true, state: { error: error || 'Sign-in failed' } });
    }
  }, [result, navigate]);

  // Loading state while retrying
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" />
        <p className="text-purple-300 text-lg">Completing sign in...</p>
      </div>
    </div>
  );
}
